import {
  WherdClient,
  WherdCoreProvider,
  useActivation,
  useWherdClient,
  type WherdUser,
  type ThemeTokens,
} from "@wherdkit/core";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { AppState } from "react-native";
import { rnDevice, rnEventSource, rnShake, rnStorage } from "./adapters";

// React Native / Metro injects `process.env` at build time; declare it for the
// dts build (this package intentionally carries no @types/node dependency).
declare const process: { env: Record<string, string | undefined> };

// Wherd-internal override for pointing the SDK at a non-production API during
// our own local development (Expo public env). Consumers never set this — their
// builds resolve `undefined`, so the client falls back to the hosted default.
const DEV_API_URL = process.env.EXPO_PUBLIC_WHERD_API_URL;

export interface WherdProviderProps {
  /** Your project's publishable public key (pk_...). */
  publicKey: string;
  user?: WherdUser;
  theme?: ThemeTokens;
  /** Override activation modes locally (otherwise server config decides). */
  activation?: { fab?: boolean; shake?: boolean };
  /**
   * Total acceleration (g) required to register a shake. Higher values demand a
   * stronger shake; defaults to the SDK's standard sensitivity (1.8).
   */
  shakeThreshold?: number;
  /** Override auto-show locally (otherwise server config decides). */
  autoShow?: { announcements?: boolean; changelog?: boolean; messages?: boolean };
  children: ReactNode;
}

// Subscribes to shake gestures when shake activation is enabled (Provider prop
// override → server config, resolved by useActivation).
function ShakeManager({ threshold }: { threshold?: number }) {
  const client = useWherdClient();
  const enabled = useActivation().shake;

  useEffect(() => {
    if (!enabled) return;
    return rnShake.subscribe(() => client.open("messages"), { threshold });
  }, [enabled, client, threshold]);

  return null;
}

// Data is pull-only (no realtime), so anything that lands while the app is
// backgrounded would otherwise stay invisible until a cold start. On return to
// the foreground, refetch messages, announcements, and changelog (badges catch
// up) and auto-open the panel for anything new that warrants it.
function ForegroundRefresh() {
  const client = useWherdClient();
  useEffect(() => {
    const sub = AppState.addEventListener("change", (next) => {
      if (next === "active") void client.foregroundRefresh();
    });
    return () => sub.remove();
  }, [client]);
  return null;
}

export function WherdProvider({
  publicKey,
  user,
  theme,
  activation,
  shakeThreshold,
  autoShow,
  children,
}: WherdProviderProps) {
  const client = useMemo(
    () =>
      new WherdClient({
        publicKey,
        apiUrl: DEV_API_URL,
        user,
        theme,
        activation,
        autoShow,
        adapters: {
          storage: rnStorage,
          device: rnDevice,
          shake: rnShake,
          eventSource: rnEventSource,
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publicKey],
  );

  const userKey = user ? `${user.id}:${user.email ?? ""}` : "";
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (user) void client.identify(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, userKey]);

  // Push theme changes into the client after mount so the widget re-themes when
  // the host app toggles its own appearance (the client is created once).
  const themeKey = JSON.stringify(theme ?? null);
  const firstTheme = useRef(true);
  useEffect(() => {
    if (firstTheme.current) {
      firstTheme.current = false;
      return;
    }
    client.setTheme(theme ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, themeKey]);

  return (
    <WherdCoreProvider client={client}>
      <ShakeManager threshold={shakeThreshold} />
      <ForegroundRefresh />
      {children}
    </WherdCoreProvider>
  );
}
